import { openDB, type DBSchema, type IDBPDatabase } from "idb";

/**
 * Local audit trail for offline sync.
 *
 * Every queued action (writes, file uploads, queue actions, prescriptions)
 * appends events here: when it was queued, each retry with its error, and the
 * final result (synced / dropped / conflict / cancelled). Purely device-local
 * so it still works with no network — that's the whole point.
 */

const DB_NAME = "doctorsx-sync-audit";
const DB_VERSION = 1;
const STORE = "events";
const MAX_EVENTS = 800;

export type AuditSource = "write" | "upload" | "queue" | "prescription" | "share";
export type AuditStatus =
  | "queued"
  | "retry"
  | "synced"
  | "failed"
  | "dropped"
  | "conflict"
  | "cancelled";

export interface SyncAuditEvent {
  id: string;
  at: number;
  source: AuditSource;
  /** Outbox key / item id so all events for one action group together. */
  ref: string;
  status: AuditStatus;
  label?: string;
  table?: string;
  op?: string;
  attempt?: number;
  error?: string;
}

interface AuditSchema extends DBSchema {
  events: { key: string; value: SyncAuditEvent; indexes: { at: number } };
}

let dbPromise: Promise<IDBPDatabase<AuditSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<AuditSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "id" });
          store.createIndex("at", "at");
        }
      },
    });
  }
  return dbPromise;
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeSyncAudit(fn: Listener) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore listener errors */
    }
  });
}

function uid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Append an event. Never throws — auditing must not break a sync run. */
export async function logSyncEvent(entry: Omit<SyncAuditEvent, "id" | "at">) {
  try {
    const db = await getDb();
    await db.put(STORE, {
      ...entry,
      error: entry.error ? entry.error.slice(0, 400) : undefined,
      id: uid(),
      at: Date.now(),
    });

    const count = await db.count(STORE);
    if (count > MAX_EVENTS) {
      const oldest = await db.getAllFromIndex(STORE, "at", undefined, count - MAX_EVENTS);
      for (const row of oldest) await db.delete(STORE, row.id);
    }
    emit();
  } catch {
    /* storage unavailable / private mode — auditing is best-effort */
  }
}

/** Fire-and-forget variant for use inside sync loops. */
export function trackSync(entry: Omit<SyncAuditEvent, "id" | "at">) {
  void logSyncEvent(entry);
}

export async function listSyncEvents(): Promise<SyncAuditEvent[]> {
  try {
    const db = await getDb();
    const all = await db.getAll(STORE);
    return all.sort((a, b) => b.at - a.at);
  } catch {
    return [];
  }
}

export async function clearSyncEvents() {
  try {
    const db = await getDb();
    await db.clear(STORE);
    emit();
  } catch {
    /* ignore */
  }
}

export interface AuditAction {
  ref: string;
  source: AuditSource;
  label?: string;
  table?: string;
  op?: string;
  queuedAt: number;
  lastAt: number;
  attempts: number;
  status: AuditStatus;
  lastError?: string;
  events: SyncAuditEvent[];
}

/** Group raw events into one row per queued action, newest first. */
export function groupSyncEvents(events: SyncAuditEvent[]): AuditAction[] {
  const map = new Map<string, AuditAction>();
  // Oldest → newest so the last status wins.
  for (const e of [...events].sort((a, b) => a.at - b.at)) {
    const key = `${e.source}:${e.ref}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        ref: e.ref,
        source: e.source,
        label: e.label,
        table: e.table,
        op: e.op,
        queuedAt: e.at,
        lastAt: e.at,
        attempts: e.status === "retry" ? 1 : 0,
        status: e.status,
        lastError: e.error,
        events: [e],
      });
      continue;
    }
    existing.lastAt = e.at;
    existing.status = e.status;
    existing.label = existing.label ?? e.label;
    existing.table = existing.table ?? e.table;
    existing.op = existing.op ?? e.op;
    if (e.status === "retry" || e.status === "failed") {
      existing.attempts = e.attempt ?? existing.attempts + 1;
    }
    if (e.error) existing.lastError = e.error;
    existing.events.push(e);
  }
  return [...map.values()].sort((a, b) => b.lastAt - a.lastAt);
}

export function exportSyncEventsCsv(events: SyncAuditEvent[]): string {
  const head = ["time", "source", "ref", "status", "attempt", "table", "op", "label", "error"];
  const rows = events.map((e) =>
    [
      new Date(e.at).toISOString(),
      e.source,
      e.ref,
      e.status,
      e.attempt ?? "",
      e.table ?? "",
      e.op ?? "",
      e.label ?? "",
      (e.error ?? "").replace(/\s+/g, " "),
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  return [head.join(","), ...rows].join("\n");
}
